/**
 * Client for vfs-service (migration/vfs-adk-generation) — the durable,
 * server-side store for a project's source code when it was created WITHOUT
 * a git connection (see App.jsx's git-free "code" creation path).
 *
 * Storage model for a vfs-backed project: `indexedDbFileStorage` stays the
 * thing Monaco/the file tree read from directly (unchanged), but every write
 * is now ALSO pushed here (see `indexedDbFileStorage.js`'s `saveFile`, which
 * calls `pushFile` after a successful local write when
 * `isVfsBackedProject(projectId)` is true) — vfs-service is the durable copy,
 * IndexedDB is the fast local cache Monaco already knows how to use. A
 * project's git connection (linking a real GitLab/GitHub repo and pushing
 * this content into it) is a separate, later, optional step — not built yet.
 *
 * Every vfs-service project uses the SAME id as its Console project (the
 * BFF-generated slug from `miaPlatformDataService.createProject`) — that id
 * is already git-independent (see App.jsx), which is what makes reusing it
 * here safe.
 */

import { createTwoFilesPatch } from 'diff';
import config from '../config';
import { getAuthorizedFetch } from './fetchRegistry';
import { buildAclContextHeaders } from './aclContext';
import { isBrainPath } from './projectLayout';

const VFS_BACKED_PROJECTS_KEY = 'mia_vfs_backed_projects';

/**
 * vfs-service's reserved sentinel project id for the global, cross-project
 * brain (vfs-service commit f06e38f, `src/lib/projectId.js`'s own
 * `GLOBAL_BRAIN_PROJECT_ID`) — must match that value EXACTLY, since it's
 * not a real project, just a special-cased id every `:projectId`-scoped
 * route (including this file's own `projectUrl()`) treats like any other.
 */
export const GLOBAL_BRAIN_PROJECT_ID = '_global-brain';

/**
 * Task #180 — directories a vfs-backed project's bulk sync (`listFilePaths`/
 * `fetchAllFiles`/`fetchServiceFiles`, the path that actually populates
 * IndexedDB — see their own docs) never recurses into, regardless of
 * whether vfs-service happens to have anything under them. Under normal
 * agent-driven code generation these basically never appear (package
 * managers/build tools are rejected outright by vfs-service's own exec/bash
 * tools — the preview build's `npm install`/`npm run build` output lives
 * entirely in container-live/preview-runner's own separate copy on disk,
 * never written back into vfs-service), but a REAL external repo brought in
 * via `vfs_clone`/an archive import can absolutely have one committed —
 * dependency trees and build output are exactly the kind of thing that
 * balloons a bulk fetch from "a few dozen source files" to "thousands of
 * individual HTTP GETs, one per file, and megabytes sitting in IndexedDB for
 * content nobody ever needed to edit."
 *
 * `.git` isn't in this list on purpose — vfs-service's own tree listing
 * already excludes it server-side (`fsOps.js`'s own hard-coded skip), so
 * filtering it again here would be dead code, not defense-in-depth.
 */
const SYNC_EXCLUDED_DIR_NAMES = new Set([
  'node_modules',
  'dist',
  'build',
  'out',
  '.next',
  '.nuxt',
  '.svelte-kit',
  '.output',
  '.cache',
  '.parcel-cache',
  '.turbo',
  '.vercel',
  '.netlify',
  'coverage',
  'target',       // Rust/Java/Maven/Gradle build output
  '__pycache__',
  '.venv',
  'venv',
]);

/** True when a project path lies under a folder the bulk sync never reads (node_modules, dist, …). */
export function isSyncExcludedPath(path) {
  const segments = String(path || '').split('/');
  return segments.slice(0, -1).some((segment) => SYNC_EXCLUDED_DIR_NAMES.has(segment));
}

function isConfigured() {
  return Boolean(config.vfsServiceUrl);
}

function projectUrl(projectId, path = '') {
  return `${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/projects/${encodeURIComponent(projectId)}${path}`;
}

function headers(additional = {}) {
  return {
    'Accept': 'application/json',
    ...buildAclContextHeaders(),
    ...additional,
  };
}

/**
 * A write vfs-service refused because the path is in a repo flagged read-only (`403 { error, code: 'repo_read_only',
 * repo }`): an Error with the server's own sentence ("services/api is read-only in this project: …"), or null for any
 * other failure (the caller keeps its own message).
 */
export function readOnlyRefusal(status, detail) {
  if (status !== 403 || !detail) { return null; }
  let body = null;
  try { body = typeof detail === 'string' ? JSON.parse(detail) : detail; } catch { return null; }
  if (body?.code !== 'repo_read_only') { return null; }
  const error = new Error(body.error || `${body.repo || 'This repo'} is read-only in this project.`);
  error.code = 'repo_read_only';
  error.status = 403;
  error.statusCode = 403;
  error.repo = body.repo || null;
  return error;
}

/**
 * Builds the `vfsSource` payload react-container-live's `/api/render` (and
 * `/api/preview/:sessionId/update`) expect when a service's source of truth
 * is vfs-service rather than pushed `sourceCode` — the same
 * `{vfsSource: {vfsProjectId, vfsServiceName}}` shape was hand-built at three
 * separate call sites in Canvas.jsx; kept here as one definition so they
 * can't drift apart.
 */
export function buildVfsSourcePayload(vfsProjectId, vfsServiceName) {
  return { vfsSource: { vfsProjectId, vfsServiceName } };
}

/** Marks `projectId` as vfs-backed (created without a git connection) — read by indexedDbFileStorage's write-through. */
export function markProjectVfsBacked(projectId) {
  try {
    const raw = localStorage.getItem(VFS_BACKED_PROJECTS_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    if (!ids.includes(projectId)) {
      ids.push(projectId);
      localStorage.setItem(VFS_BACKED_PROJECTS_KEY, JSON.stringify(ids));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('vfsClient: failed to record vfs-backed project', error);
  }
}

export function isVfsBackedProject(projectId) {
  if (!projectId) {
    return false;
  }
  try {
    const raw = localStorage.getItem(VFS_BACKED_PROJECTS_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return ids.includes(projectId);
  } catch {
    return false;
  }
}

function vfsServicesKey(projectId) {
  return `mia_vfs_services::${projectId}`;
}

function vfsServiceDisplayNamesKey(projectId) {
  return `mia_vfs_service_display_names::${projectId}`;
}

/**
 * A service's real vfs folder name (its identity — used for selection, file
 * paths, everything functional) and its human-friendly DISPLAY name are kept
 * separate on purpose: the default service created for a brand-new project
 * is 'services/app' (a real subfolder, not the project root — see task
 * #147's restructure: the project root is reserved for `.mia/project.json`
 * metadata only, adk-be-app's vfs_tools.py now refuses any write outside a
 * service subfolder). Even so, a real subfolder can't be trivially renamed
 * mid-project without moving every file in it, so relabeling is still used:
 * it lets a project's default service show something meaningful (e.g.
 * "Pomodoro Timer") once the chat reveals what's being built, without
 * physically renaming the folder underneath it.
 */
export function setVfsServiceDisplayName(projectId, serviceName, displayName) {
  try {
    const raw = localStorage.getItem(vfsServiceDisplayNamesKey(projectId));
    const map = raw ? JSON.parse(raw) : {};
    map[serviceName] = displayName;
    localStorage.setItem(vfsServiceDisplayNamesKey(projectId), JSON.stringify(map));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('vfsClient: failed to save service display name', error);
  }
}

/** Returns the saved display name for `serviceName`, or null if none was ever set. */
export function getVfsServiceDisplayName(projectId, serviceName) {
  if (!projectId) {
    return null;
  }
  try {
    const raw = localStorage.getItem(vfsServiceDisplayNamesKey(projectId));
    const map = raw ? JSON.parse(raw) : {};
    return map[serviceName] || null;
  } catch {
    return null;
  }
}

/**
 * Registers a vfs-only service (one created via CreateServiceModal's
 * clone/archive-import path, living under a project subfolder — see
 * gitTemplateClone.js — with no Console/GitLab registration at all) so it
 * can be found again: Console's own `configuration.services` will never
 * list it, since Console never learned about it. Canvas.jsx merges this
 * list into `configuration.services` for its own state and for the
 * UnifiedTopBar service dropdowns, which otherwise only enumerate
 * Console-registered services.
 */
export function registerVfsService(projectId, serviceName) {
  try {
    const raw = localStorage.getItem(vfsServicesKey(projectId));
    const names = raw ? JSON.parse(raw) : [];
    if (!names.includes(serviceName)) {
      names.push(serviceName);
      localStorage.setItem(vfsServicesKey(projectId), JSON.stringify(names));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('vfsClient: failed to register vfs service', error);
  }
}

/** Returns locally-registered vfs-only service names for `projectId` (see registerVfsService). */
export function getVfsServiceNames(projectId) {
  if (!projectId) {
    return [];
  }
  try {
    const raw = localStorage.getItem(vfsServicesKey(projectId));
    // The brain is never a service (utils/projectLayout.js), also when this browser registered it before.
    return (raw ? JSON.parse(raw) : []).filter((name) => !isBrainPath(name));
  } catch {
    return [];
  }
}

/**
 * Provision a new vfs-service project. Idempotent in practice: a 409/already-
 * exists response from vfs-service is treated as success (e.g. a retried
 * creation flow), everything else throws.
 */
export async function createProject(projectId) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(`${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/projects`, {
    method: 'POST',
    headers: headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ projectId }),
  });
  if (!response.ok && response.status !== 409) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service createProject failed (${response.status}): ${detail}`);
  }
  return true;
}

/**
 * Whole-file create/replace — mirrors indexedDbFileStorage's own full-content
 * save model. Bypasses vfs-service's collaborative Yjs doc layer entirely
 * (writes straight to disk — see vfs-service's own PUT /file doc comment:
 * "not the normal incremental-edit path, that's POST /patch") — which means
 * ANY OTHER CLIENT with that file already open in a live doc session (e.g.
 * vfs-service's vfs-fe test console, or a second mia-flow-fe tab, or an
 * adk-be-app agent turn using vfs_patch) will NOT see this write until it
 * closes and reopens that file. Use `patchFile` below for an existing file
 * instead wherever the previous content is known — this is really only
 * correct for a brand-new file with no possible concurrent viewer yet.
 */
export async function writeFile(projectId, filePath, content) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/file')}?path=${encodeURIComponent(filePath)}`,
    {
      method: 'PUT',
      headers: headers({ 'content-type': 'text/plain' }),
      body: content,
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const refused = readOnlyRefusal(response.status, detail);
    if (refused) { throw refused; }
    throw new Error(`vfs-service writeFile failed (${response.status}) for ${filePath}: ${detail}`);
  }
  return true;
}

/**
 * Move (or rename) a file or folder inside a project. vfs-service commits the change and notifies other
 * clients. NOTE: it silently OVERWRITES an existing destination, so callers must check that the target path
 * is free first (RepositoryTree does).
 * @param {string} projectId
 * @param {string} from project-relative source path
 * @param {string} to project-relative destination path
 */
export async function moveEntry(projectId, from, to) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(projectUrl(projectId, '/move'), {
    method: 'POST',
    headers: headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ from, to }),
  });
  if (!response.ok) {
    let detail = '';
    try { detail = (await response.json()).error || ''; } catch { detail = await response.text().catch(() => ''); }
    throw new Error(detail || `vfs-service move failed (${response.status})`);
  }
  return true;
}

/**
 * Whole-file create/replace for BINARY content (a PDF, a Word file…). Same write path and caveats as
 * `writeFile`; the body is sent as raw bytes with `encoding=base64`, which tells vfs-service to store
 * the bytes exactly as received instead of treating them as UTF-8 text.
 * @param {string} projectId
 * @param {string} filePath project-relative path
 * @param {Blob|ArrayBuffer|Uint8Array} data
 */
export async function writeBinaryFile(projectId, filePath, data) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/file')}?path=${encodeURIComponent(filePath)}&encoding=base64`,
    {
      method: 'PUT',
      headers: headers({ 'content-type': 'application/octet-stream' }),
      body: data,
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const refused = readOnlyRefusal(response.status, detail);
    if (refused) { throw refused; }
    throw new Error(`vfs-service writeBinaryFile failed (${response.status}) for ${filePath}: ${detail}`);
  }
  return true;
}

/**
 * Read a file as raw bytes (a PDF, an image…) and return it as a Blob, or null when it does not exist.
 * `readFile` decodes the body as UTF-8 text, which corrupts binary content; this asks vfs-service for
 * `encoding=base64` and decodes it back to the exact bytes.
 * @param {string} projectId
 * @param {string} filePath project-relative path
 * @param {string} [mimeType] type of the returned Blob
 * @returns {Promise<Blob|null>}
 */
export async function readBinaryFile(projectId, filePath, mimeType = 'application/octet-stream') {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/file')}?path=${encodeURIComponent(filePath)}&encoding=base64`,
    { headers: headers({ Accept: 'text/plain' }) }
  );
  if (response.status === 404) { return null; }
  if (!response.ok) {
    throw new Error(`vfs-service readBinaryFile failed (${response.status}) for ${filePath}`);
  }
  let binary = atob((await response.text()).trim());
  // A document whose BYTES were stored as its base64 TEXT (an old upload path did that) comes back as the
  // base64 characters themselves: the PDF/zip/OLE signature spelled in base64. Decode once more so it opens.
  if (/^(JVBERi|UEsDB|0M8R4K)[A-Za-z0-9+/\s]+={0,2}\s*$/.test(binary)) {
    try { binary = atob(binary.replace(/\s+/g, '')); } catch { /* not really base64: keep as is */ }
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) { bytes[i] = binary.charCodeAt(i); }
  return new Blob([bytes], { type: mimeType });
}

/**
 * Apply an edit to an EXISTING file via vfs-service's collaborative-edit
 * path (POST /patch), so it merges through the file's live Yjs doc — the
 * one write path that keeps any other client with this file open (vfs-fe,
 * another tab, an agent) in sync in real time, unlike `writeFile` above.
 * Generates the unified diff client-side with the same `diff` npm package
 * (same major version — see package.json) vfs-service itself uses server-
 * side to apply it, so the format is guaranteed compatible.
 *
 * On a 409 (the diff's context no longer matches — someone else changed the
 * file server-side since `oldContent` was read), throws a `PatchConflictError`-
 * shaped error; the caller (indexedDbFileStorage's write-through) treats this
 * as non-fatal, same as any other write-through failure — IndexedDB, the
 * local source of truth for the browser tab, is unaffected either way.
 */
export async function patchFile(projectId, filePath, oldContent, newContent) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  if (oldContent === newContent) {
    return true;
  }
  const diff = createTwoFilesPatch(filePath, filePath, oldContent, newContent);
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/patch'),
    {
      method: 'POST',
      headers: headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({ path: filePath, diff }),
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const refused = readOnlyRefusal(response.status, detail);
    if (refused) { throw refused; }
    const error = new Error(`vfs-service patchFile failed (${response.status}) for ${filePath}: ${detail}`);
    error.statusCode = response.status;
    throw error;
  }
  return true;
}

/** Creates an empty directory (parents included by default) — see vfs-service's POST /mkdir. */
export async function mkdir(projectId, dirPath, { recursive = true } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/mkdir'),
    {
      method: 'POST',
      headers: headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({ path: dirPath, recursive }),
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const refused = readOnlyRefusal(response.status, detail);
    if (refused) { throw refused; }
    throw new Error(`vfs-service mkdir failed (${response.status}) for ${dirPath}: ${detail}`);
  }
  return true;
}

/** Deletes a file or (with `recursive: true`) a whole directory — see vfs-service's DELETE /entry. */
export async function deleteEntry(projectId, entryPath, { recursive = false } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/entry'),
    {
      method: 'DELETE',
      headers: headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({ path: entryPath, recursive }),
    }
  );
  if (!response.ok && response.status !== 204) {
    const detail = await response.text().catch(() => '');
    const refused = readOnlyRefusal(response.status, detail);
    if (refused) { throw refused; }
    throw new Error(`vfs-service deleteEntry failed (${response.status}) for ${entryPath}: ${detail}`);
  }
  return true;
}

export async function readFile(projectId, filePath) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/file')}?path=${encodeURIComponent(filePath)}`,
    { headers: headers() }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service readFile failed (${response.status}) for ${filePath}: ${detail}`);
  }
  return response.text();
}

/**
 * Path of the vfs metadata file that holds a project's Dashboard-facing
 * display name/description/technologies — see `readProjectMetadata` below.
 * Kept under a hidden `.mia/` folder so it doesn't clutter the user's own
 * source tree or file explorer.
 */
export const PROJECT_METADATA_PATH = '.mia/project.json';

/**
 * Writes a default `.mia/project.json` immediately at project-provisioning
 * time (see quickProjectCreation.js) — a project must never exist, even
 * transiently, with no metadata file at all (the Files browser used to show
 * "No files yet." and the Dashboard placeholder text right up until the
 * agent got around to calling `vfs_set_project_metadata`, which could be
 * several turns after creation, or never). This is a starting point only:
 * the agent's own `vfs_set_project_metadata` call — and this same
 * `.mia/project.json` — get overwritten/updated once the app's real purpose
 * is known; this call must never be what runs THEN, only at creation.
 * Best-effort: a failure here is logged, not thrown — a project existing
 * without this file is a UX regression, not a reason to fail creation.
 */
export async function writeDefaultProjectMetadata(projectId, name, description = '', conversationId = null) {
  try {
    // conversationId mirrors adk-be-app's own _write_default_project_metadata
    // shape (always writes this key, even null) — written here too, not left
    // to the agent's later vfs_set_project_metadata call, so a project
    // created via this quick-create path (see quickProjectCreation.js) is
    // never left permanently unbound to its conversation if the agent never
    // gets around to that call (see the Home->build race it was fixed for).
    await writeFile(
      projectId,
      PROJECT_METADATA_PATH,
      JSON.stringify({ name, description, technologies: [], conversationId }, null, 2),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('vfsClient: failed to write default project metadata', error);
  }
}

/**
 * Reads `.mia/project.json` — the agent-authored source of truth for a vfs
 * project's Dashboard display name/description/technologies (see
 * `vfs_set_project_metadata` in adk-be-app). Most existing projects won't
 * have this file yet, so a missing file (404, or any other readFile
 * failure) resolves to `null` rather than throwing — callers should fall
 * back to whatever placeholder/derived values they already use. Malformed
 * JSON also resolves to `null` rather than throwing.
 * @returns {Promise<{name?: string, description?: string, technologies?: string[]}|null>}
 */
export async function readProjectMetadata(projectId) {
  if (!isConfigured() || !projectId) {
    return null;
  }
  let raw;
  try {
    raw = await readFile(projectId, PROJECT_METADATA_PATH);
  } catch (_error) {
    // Missing file (most projects) or any other read failure — no metadata.
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
}

/**
 * `.mia/project.json` with the answer's status, for callers that must tell "no access" from "no file": `{ status,
 * metadata }` (metadata null unless 200 and valid JSON). Never throws (a network error is status 0).
 */
export async function fetchProjectMetadata(projectId) {
  if (!isConfigured() || !projectId) {
    return { status: 0, metadata: null };
  }
  try {
    const response = await getAuthorizedFetch()(
      `${projectUrl(projectId, '/file')}?path=${encodeURIComponent(PROJECT_METADATA_PATH)}`,
      { headers: headers() }
    );
    if (!response.ok) {
      return { status: response.status, metadata: null };
    }
    const parsed = JSON.parse(await response.text());
    return { status: response.status, metadata: parsed && typeof parsed === 'object' ? parsed : null };
  } catch (_error) {
    return { status: 0, metadata: null };
  }
}

/**
 * Reverse of `readProjectMetadata`: given a real ADK conversationId, finds
 * the vfs project it's bound to (if any) — vfs-service scans every
 * project's own `.mia/project.json` for a matching `conversationId`. Used
 * to enable the Code button on a RESUMED /chat conversation, whose project
 * binding would otherwise never reach the client: the vendored ChatWidget's
 * history replay (on a plain page load, no live turn) doesn't re-emit the
 * original tool-call responses `onProjectBound` normally reads live — see
 * VfsChatPanel.jsx. Resolves to `null` on a 404 (no bound project yet) or
 * any other failure, same "never throw for a missing/absent case" contract
 * as `readProjectMetadata`.
 * @returns {Promise<string|null>}
 */
export async function findProjectByConversationId(conversationId) {
  if (!isConfigured() || !conversationId) {
    return null;
  }
  try {
    const response = await getAuthorizedFetch()(
      `${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/projects/by-conversation/${encodeURIComponent(conversationId)}`,
      { headers: headers() }
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data && typeof data.projectId === 'string' ? data.projectId : null;
  } catch (_error) {
    return null;
  }
}

/**
 * Merges `updates` (e.g. `{name}`, `{description}`, or both) into the
 * existing `.mia/project.json`, preserving whatever fields aren't part of
 * this update (in particular `technologies`, which this call never touches).
 * DashboardView's rename/description-edit UI is the caller — its displayed
 * name/description prefer this file over the Console project record (see
 * DashboardView's own `displayName`/`displayDescription`), so a rename that
 * only updated the Console record via miaPlatformDataService.updateProject
 * would appear to silently fail to the user on next load.
 */
export async function updateProjectMetadata(projectId, updates) {
  const current = (await readProjectMetadata(projectId)) || {};
  const merged = { ...current, ...updates };
  await writeFile(projectId, PROJECT_METADATA_PATH, JSON.stringify(merged, null, 2));
  return merged;
}

/**
 * Immediate children (files AND directories, one level deep) of `path` —
 * what VfsChatPanel's `/files [path]` slash command shows for a directory.
 * Unlike `listFilePaths` below, this does NOT recurse or flatten: it is a
 * `ls`, not a full-tree walk, and it keeps directories in the result so a
 * command result can tell the two apart.
 * @returns {Promise<{isDirectory: boolean, entries: Array<{name: string, type: 'file'|'directory'}>}>}
 *   `isDirectory` is false when `path` itself names a file, in which case
 *   `entries` is empty — callers wanting that file's content should follow
 *   up with `readFile`.
 */
export async function listDirectoryEntries(projectId, path = '.') {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/tree')}?path=${encodeURIComponent(path)}&depth=1`,
    { headers: headers() }
  );
  // A folder that does not exist (yet) lists as empty: not an error (e.g. `brain/outputs` before the first output).
  if (response.status === 404) {
    return { entries: [], isDirectory: false, missing: true };
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service listDirectoryEntries failed (${response.status}) for ${path}: ${detail}`);
  }
  const node = await response.json();
  // vfs-service's /tree returns the node rooted AT `path` itself — a file
  // path comes back with no `children` (or `type: 'file'`), a directory with
  // a `children` array (possibly empty).
  if (!node || node.type === 'file' || !Array.isArray(node.children)) {
    return { entries: [], isDirectory: false };
  }
  return {
    entries: node.children.map((child) => ({ name: child.name, type: child.type === 'directory' ? 'directory' : 'file' })),
    isDirectory: true,
  };
}

/**
 * The tree node at `path`, `depth` levels deep (vfs-service's GET /tree: the node rooted AT `path`, with `children`
 * for a directory), or null when the path does not exist. Ask for a parent once instead of probing children that may
 * not exist: each missing one is a 404 in the browser console.
 */
export async function readTree(projectId, path = '.', depth = 1) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/tree')}?path=${encodeURIComponent(path)}&depth=${depth}`,
    { headers: headers() }
  );
  if (response.status === 404) { return null; }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service readTree failed (${response.status}) for ${path}: ${detail}`);
  }
  return response.json();
}

/**
 * One folder's direct children, for the file tree that loads a folder when it is opened: vfs-service's
 * `GET /tree/children` (folders first, at most `limit` from `cursor`; node_modules, dist and the like come back
 * `collapsed` with a `childCount`). A vfs-service without that route yet answers from `GET /tree?depth=1`, the
 * dependency folders collapsed here. A folder that does not exist lists as empty, with `missing: true`.
 * @returns {Promise<{entries: Array<{name: string, type: 'file'|'directory', size?: number, collapsed?: boolean,
 *   childCount?: number}>, total: number, truncated: boolean, nextCursor: number|null, missing?: boolean}>}
 */
export async function listFolder(projectId, path = '.', { limit, cursor } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const empty = { entries: [], total: 0, truncated: false, nextCursor: null };
  if (isPseudoServicePath(path)) { return { ...empty, missing: true }; }
  const query = new URLSearchParams({ path: path || '.' });
  if (limit) { query.set('limit', String(limit)); }
  if (cursor) { query.set('cursor', String(cursor)); }
  const response = await getAuthorizedFetch()(`${projectUrl(projectId, '/tree/children')}?${query}`, { headers: headers() });
  const isJson = (response.headers?.get?.('content-type') || '').includes('json');
  if (response.status === 404 && !isJson) {
    // An older vfs-service (no such route): the same folder through the one-level tree listing.
    const node = await readTree(projectId, path || '.', 1);
    if (!node) { return { ...empty, missing: true }; }
    const entries = (node.children || []).map((child) => (
      child.type === 'directory' && SYNC_EXCLUDED_DIR_NAMES.has(child.name) ? { ...child, collapsed: true } : child
    ));
    return { entries, total: entries.length, truncated: false, nextCursor: null };
  }
  if (response.status === 404) { return { ...empty, missing: true }; }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service listFolder failed (${response.status}) for ${path}: ${detail}`);
  }
  const listing = await response.json();
  return {
    entries: listing.entries || [],
    total: listing.total ?? (listing.entries || []).length,
    truncated: Boolean(listing.truncated),
    nextCursor: listing.nextCursor ?? null,
  };
}

/**
 * True when `serviceName` has no real, buildable content yet — either the
 * folder doesn't exist at all (vfs-service 404s on it — a brand-new
 * project's default service, before the agent's first write), or it exists
 * but its only entries are dotfile markers (e.g. the `.gitkeep`
 * CreateServiceModal.jsx writes just to materialize the folder). A lone
 * `README.md` or any other non-dotfile entry counts as "has content" even
 * if it wouldn't actually build — this only needs to catch the genuinely
 * untouched-scaffold case, not every possible broken project.
 *
 * Used to short-circuit BEFORE triggering a real preview-runner build (and
 * before starting its live log stream) for a service nobody has written
 * code into yet — the friendly empty state, not a build attempt doomed to
 * fail with a driver-detection error, with real (if harmless) log noise
 * along the way. See Canvas.jsx's preview-tab rendering.
 */
export async function isServiceEmpty(projectId, serviceName) {
  try {
    const { isDirectory, entries } = await listDirectoryEntries(projectId, serviceName);
    if (!isDirectory) {
      return true;
    }
    return entries.every((entry) => entry.name.startsWith('.'));
  } catch (_error) {
    // Path doesn't exist (404) or any other read failure — treat as empty
    // rather than letting the error surface as something scarier later.
    return true;
  }
}

/**
 * Flat list of file paths under the project (directories excluded), for
 * seeding IndexedDB on first load of a vfs-backed project. `depth` matches
 * vfs-service's own default of 1 level unless overridden — pass a larger
 * value to seed a deeper scaffold in one call.
 */
/**
 * "Project Configuration" is a pseudo-resource of the Console era (type 'configuration'), never a folder in vfs-service:
 * asking for it only gives a 404 "Path does not exist: Project Configuration" in the browser console.
 */
export function isPseudoServicePath(path) {
  const first = String(path || '').split('/')[0];
  return first === 'Project Configuration' || first === 'project-configuration';
}

export async function listFilePaths(projectId, { path = '.', depth = 10 } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  if (isPseudoServicePath(path)) { return []; }
  const response = await getAuthorizedFetch()(
    `${projectUrl(projectId, '/tree')}?path=${encodeURIComponent(path)}&depth=${depth}`,
    { headers: headers() }
  );
  if (response.status === 404) {
    // A brand-new service folder (e.g. `services/app` right after project
    // creation, before the agent or the user has written a first file) has
    // no vfs-service tree entry at all yet — vfs-service reports that as a
    // 404 "Path does not exist", not an empty directory. That's a real,
    // expected, common state (every fresh project starts here), not a
    // failure: treat it as zero files instead of throwing. Previously this
    // surfaced as a console error ("vfs-service full resync failed... Path
    // does not exist") on every single new project/service, with nothing
    // actually wrong.
    return [];
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service listFilePaths failed (${response.status}): ${detail}`);
  }
  const tree = await response.json();
  const paths = [];
  // The returned tree is rooted AT `path` (its own `.name` is just the last
  // path segment, not the full path) — seed the walk with `path` itself
  // (when not the project root) so returned paths are project-root-relative,
  // matching what writeFile/readFile expect.
  const initialPrefix = path && path !== '.' ? path.replace(/^\/+|\/+$/g, '') : '';
  const walk = (node, prefix) => {
    if (!node) {
      return;
    }
    const children = node.children || [];
    for (const child of children) {
      if (child.type === 'directory' && SYNC_EXCLUDED_DIR_NAMES.has(child.name)) {
        // Task #180 — never descend into a build/cache/dependency directory
        // (node_modules, dist, a real .git-equivalent brought in via
        // vfs_clone, etc.): every file under it would otherwise become its
        // own individual readFile() call below, and end up cached in
        // IndexedDB for content nobody ever asked to edit.
        continue;
      }
      const childPath = prefix ? `${prefix}/${child.name}` : child.name;
      if (child.type === 'directory') {
        walk(child, childPath);
      } else {
        paths.push(childPath);
      }
    }
  };
  walk(tree, initialPrefix);
  return paths;
}

/**
 * Fetch every file's content into a flat `{ [path]: content }` object — the
 * same shape Canvas.jsx's `fetchAllRepositoryFiles` produces from a git pull,
 * so a vfs-backed project's Preview pipeline can call this instead without
 * any downstream change (detectServiceType, IndexedDB seeding, container
 * spawning all operate on that plain path→content object regardless of
 * where it came from).
 */
export async function fetchAllFiles(projectId, onProgress = null, { path = '.' } = {}) {
  const paths = await listFilePaths(projectId, { path });
  const files = {};
  for (let i = 0; i < paths.length; i += 1) {
    const filePath = paths[i];
    try {
      files[filePath] = await readFile(projectId, filePath);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`vfsClient: failed to read ${filePath} while fetching all files`, error);
    }
    if (onProgress) {
      onProgress({ current: i + 1, total: paths.length, currentFile: filePath, percentage: Math.round(((i + 1) / paths.length) * 100) });
    }
  }
  return files;
}

/**
 * Fetch every file under a project's `serviceName` subfolder into a flat
 * `{ [serviceRelativePath]: content }` object — keys are relative to the
 * SERVICE, not the project (the `serviceName/` prefix is stripped), matching
 * how indexedDbFileStorage keys files (`${projectId}::${serviceName}::${filePath}`,
 * where filePath never includes the service's own name — same convention a
 * git-backed service already uses, since a git repo's root already IS the
 * service root, no extra nesting). Use this instead of `fetchAllFiles`
 * everywhere a vfs-backed SERVICE's files are being read into mia-flow-fe —
 * `fetchAllFiles` at the bare project root would both leak every OTHER
 * service's files into this one's IndexedDB namespace on a multi-service
 * project, and leave the `serviceName/` prefix baked into every path (the
 * literal cause of an extra `serviceName` folder showing up nested inside
 * itself in the file tree).
 */
export async function fetchServiceFiles(projectId, serviceName, onProgress = null) {
  const prefix = `${serviceName}/`;
  const vfsPaths = await listFilePaths(projectId, { path: serviceName });
  const files = {};
  for (let i = 0; i < vfsPaths.length; i += 1) {
    const vfsPath = vfsPaths[i];
    const relativePath = vfsPath.startsWith(prefix) ? vfsPath.slice(prefix.length) : vfsPath;
    // A service's own .mia/ (comments, handoff notes, suggestions: collaboration metadata versioned with the code) is
    // shown in Flow's panels, not in the service's file tree.
    if (relativePath === '.mia' || relativePath.startsWith('.mia/')) {
      continue;
    }
    try {
      files[relativePath] = await readFile(projectId, vfsPath);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`vfsClient: failed to read ${vfsPath} while fetching service files`, error);
    }
    if (onProgress) {
      onProgress({ current: i + 1, total: vfsPaths.length, currentFile: relativePath, percentage: Math.round(((i + 1) / vfsPaths.length) * 100) });
    }
  }
  return files;
}

/**
 * Commit log for the project (or a single `path` within it, when given) —
 * see vfs-service's gitHistory.js `getLog`/`parseLogOutput` for the source of
 * truth. Each entry: `{ sha, actorId, actorType, message, timestamp }` (sha
 * is the full 40-char hex, timestamp is an ISO-8601 string from `%aI`).
 * Newest-first (git log's natural order).
 */
export async function getHistory(projectId, path) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const query = path ? `?path=${encodeURIComponent(path)}` : '';
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, `/history${query}`),
    { headers: headers() }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service getHistory failed (${response.status}): ${detail}`);
  }
  return response.json();
}

/**
 * Which AI touched the project (vfs-service `.mia/provenance.jsonl`): `{ summary, shadow, canSeeTimeline,
 * timeline, retentionDays }`. `summary` is null when nothing was recorded; `timeline` (newest first) is null for
 * viewers. Filters: `action` (edit, chat-turn, publish…), `tool` (client kind: claude-code, flow-agent…), `before`
 * (ISO timestamp, for paging), `limit`. Only metadata, never prompts or file contents.
 */
export async function getProvenance(projectId, { limit, action, tool, before } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const params = new URLSearchParams();
  if (limit) { params.set('limit', String(limit)); }
  if (action) { params.set('action', action); }
  if (tool) { params.set('tool', tool); }
  if (before) { params.set('before', before); }
  const query = params.toString() ? `?${params}` : '';
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, `/provenance${query}`),
    { headers: headers() }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service getProvenance failed (${response.status}): ${detail}`);
  }
  return response.json();
}

/** The AI provenance summary alone: `{ summary, shadow }` (anyone who may read the project). */
export async function getProvenanceSummary(projectId) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/provenance/summary'),
    { headers: headers() }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service getProvenanceSummary failed (${response.status}): ${detail}`);
  }
  return response.json();
}

/**
 * Searches the project's files (vfs-service `GET /grep`, ripgrep; `node_modules` and the repositories' own metadata
 * are never searched): `[{ path, line, column, match }]`, at most `maxResults`. `glob` narrows it (`services/**`).
 */
export async function grepProject(projectId, { pattern, glob, caseInsensitive = false, maxResults = 200 } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const params = new URLSearchParams({ pattern, maxResults: String(maxResults) });
  if (glob) { params.set('glob', glob); }
  if (caseInsensitive) { params.set('caseInsensitive', 'true'); }
  const response = await getAuthorizedFetch()(projectUrl(projectId, `/grep?${params.toString()}`), { headers: headers() });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service grep failed (${response.status}): ${detail}`);
  }
  const answer = await response.json().catch(() => null);
  return Array.isArray(answer) ? answer : [];
}

/**
 * Records a web-app event in the project's AI provenance (vfs-service records it with client `flow-web` and the
 * person from the gateway's identity headers). Allowed from the web: `publish`, `deploy`, `preview`.
 * e.g. `{ action: 'publish', target: { deploy: { kind: 'console-next', system, version } } }`. Resolves to the event.
 */
export async function recordProvenanceEvent(projectId, body) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/provenance/events'),
    { method: 'POST', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(body || {}) }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service recordProvenanceEvent failed (${response.status}): ${detail}`);
  }
  const result = await response.json().catch(() => null);
  return result?.event || null;
}

/**
 * Plain-text unified diff for a single commit (`git show <sha>`) — every
 * file that commit touched, not scoped to one path.
 */
export async function getCommitDiff(projectId, sha) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, `/history/${encodeURIComponent(sha)}/diff`),
    { headers: headers() }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service getCommitDiff failed (${response.status}) for ${sha}: ${detail}`);
  }
  return response.text();
}

/**
 * Restore a SINGLE file (`path`) to its content at `sha` — this is a real,
 * non-destructive operation: it merges the historical content into the
 * file's live collaborative doc and flushes, which lands as a brand-new
 * commit (see vfs-service's gitHistory.js `rollback` + persistence.js
 * `flushToDisk`/`commitChange` — there is no `git reset`/force-checkout
 * involved, history is never rewritten or lost).
 *
 * This function only ever restores ONE file — `path` must be a real file
 * path, never '.' or omitted. For a whole-project rewind, use `rollbackAll`
 * below instead (a real, atomic, single-commit server-side operation) rather
 * than calling this once per changed file.
 * @returns {Promise<{path: string, restoredFrom: string, newSha: string|null}>}
 */
export async function rollback(projectId, path, sha) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  if (!path || path === '.') {
    throw new Error('vfs-service rollback requires a specific file path (no whole-project rollback exists server-side)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/history/rollback'),
    {
      method: 'POST',
      headers: headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({ path, sha }),
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service rollback failed (${response.status}) for ${path}@${sha}: ${detail}`);
  }
  return response.json();
}

/**
 * "Rewind to here" — vfs-service's `POST /history/rollback-all` (src/history/rollback.js `rollbackAll`): the project
 * back to how it was at commit `sha`, as NEW commits (history is never rewritten). A project has one git repo per
 * service (plus brain): `scope` 'project' (the default) brings every service to its last commit at that moment, 'repo'
 * only `sha`'s own. `targets` (`{ "<repo>": "<sha>" }`, e.g. a previous answer's `previous`) restores exactly those
 * repos to those commits (a redo). Files go through the live docs (an open editor follows), binary files byte for
 * byte, files added since are removed; changes not committed yet are committed first.
 * @returns {Promise<{restoredPaths: string[], restoredFrom: string, newSha: string|null,
 *   commits: Array<{repo: string, restoredFrom: string, sha: string|null, paths: number}>,
 *   untouched: Array<{repo: string, reason: string}>, previous: Object<string, string>}>}
 */
export async function rollbackAll(projectId, sha, { scope, targets } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/history/rollback-all'),
    {
      method: 'POST',
      headers: headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({ sha, ...(scope ? { scope } : {}), ...(targets ? { targets } : {}) }),
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service rollbackAll failed (${response.status}) for ${sha}: ${detail}`);
  }
  return response.json();
}

/**
 * The app's versions (vfs-service src/history/versions.js): named points of the code of every service, saved by hand
 * or on Publish. `{ versions: [{ id, name, note, createdAt, by: { actorId, actorType }, trigger: 'manual'|'publish',
 * deploy?, repos: { "<repo>": "<sha>" }, current, missing: [repo] }] }`, newest first.
 */
async function versionsCall(projectId, path, { method = 'GET', body } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(projectUrl(projectId, path), {
    method,
    headers: headers(body !== undefined ? { 'content-type': 'application/json' } : {}),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `vfs-service ${method} ${path} failed (${response.status})`);
    error.status = response.status;
    error.code = data?.code || null;
    error.version = data?.version || null;
    throw error;
  }
  return data || {};
}

export const listVersions = (projectId) => versionsCall(projectId, '/versions');
/** Saves the code as it is now: `{ version }`; an Error with `code` 'unchanged' (and `version`), 'name_taken' or 'nothing'. */
export const saveVersion = (projectId, { name, note } = {}) => versionsCall(projectId, '/versions', { method: 'POST', body: { ...(name ? { name } : {}), ...(note ? { note } : {}) } });
/** Every service back to the version, as new commits: `{ version, restoredPaths, commits, untouched, previous }`. */
export const restoreVersion = (projectId, id) => versionsCall(projectId, `/versions/${encodeURIComponent(id)}/restore`, { method: 'POST', body: {} });
export const deleteVersion = (projectId, id) => versionsCall(projectId, `/versions/${encodeURIComponent(id)}`, { method: 'DELETE' });

/**
 * Working-tree diff (`git diff`), or a diff between two refs when `range`
 * is given (e.g. `${sha}..HEAD`). `stat` requests `--stat` (files-changed
 * summary), `unified` sets the context-line count (`-U<n>`).
 */
export async function getWorkingDiff(projectId, { stat, unified, range } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const params = new URLSearchParams();
  if (stat) {
    params.set('stat', 'true');
  }
  if (unified !== undefined) {
    params.set('unified', String(unified));
  }
  if (range) {
    params.set('range', range);
  }
  const query = params.toString();
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, `/git/diff${query ? `?${query}` : ''}`),
    { headers: headers() }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service getWorkingDiff failed (${response.status}): ${detail}`);
  }
  return response.text();
}

/**
 * Manual checkpoint: commits everything currently in the working tree
 * (mirrors vfs-service's own `POST /git/commit` -> `commitChange(projectId,
 * {relPath: '.', message})`). Returns the new commit sha, or `null` if there
 * was nothing to commit (clean tree).
 */
export async function commitCheckpoint(projectId, message) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    projectUrl(projectId, '/git/commit'),
    {
      method: 'POST',
      headers: headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({ message }),
    }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`vfs-service commitCheckpoint failed (${response.status}): ${detail}`);
  }
  const { sha } = await response.json();
  return sha;
}

/* ------------------------------------------------------------------------------------------------
 * Project sharing (flow-helm-chart docs/project-sharing.md, "vfs-service REST"). The caller's
 * identity comes from the gateway's x-mia-subject / x-mia-email headers, never from the body.
 * Failures throw a `SharingError` carrying the HTTP `status`, so the UI can tell "you may not do
 * this" (403) and "nothing there" (404) apart from a real failure.
 * ---------------------------------------------------------------------------------------------- */

export class SharingError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'SharingError';
    this.status = status;
  }
}

async function sharingRequest(projectId, path, { method = 'GET', body } = {}) {
  if (!isConfigured()) {
    throw new SharingError('vfs-service is not configured (vfsServiceUrl is unset)', 0);
  }
  const response = await getAuthorizedFetch()(projectUrl(projectId, path), {
    method,
    headers: headers(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.error || '';
    } catch {
      detail = '';
    }
    throw new SharingError(detail || `vfs-service ${method} ${path} failed (${response.status})`, response.status);
  }
  if (response.status === 204) {
    return null;
  }
  return response.json().catch(() => null);
}

const membersOf = (data) => (Array.isArray(data) ? data : data?.members || []);

/**
 * Who can open a project and as what: `{ role, owner, members, previewLinks? }` — `role` is the
 * caller's own ('owner' | 'editor' | 'viewer' | 'none'); `owner`/`members` only when role is not
 * none; `previewLinks` (no tokens) only for editors and the owner.
 */
export function getProjectAccess(projectId) {
  return sharingRequest(projectId, '/access');
}

/**
 * Becomes the first owner of a project that has none (created by a Flow agent, or before owners were recorded), so it
 * can be shared. Resolves to the owner; a project that already has an owner answers 409.
 */
export async function claimProject(projectId, name) {
  const body = await sharingRequest(projectId, '/claim', { method: 'POST', body: name ? { name } : {} });
  return body?.owner || null;
}

/** Owner only: adds (or changes the role of) a member by email. Resolves to the updated members. */
export async function addProjectMember(projectId, email, role) {
  return membersOf(await sharingRequest(projectId, '/members', { method: 'PUT', body: { email, role } }));
}

/**
 * Owners: the people already on this Flow whose email or name contains `query` (2+ characters), to suggest in the
 * share dialog. Resolves to `[{ email, name }]`; any failure is an empty list, the field still takes typed addresses.
 */
export async function suggestPeople(query) {
  const q = String(query || '').trim();
  if (!isConfigured() || q.length < 2) {
    return [];
  }
  try {
    const response = await getAuthorizedFetch()(`${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/people?q=${encodeURIComponent(q)}`, { headers: headers() });
    if (!response.ok) {
      return [];
    }
    const body = await response.json();
    return Array.isArray(body?.people) ? body.people : [];
  } catch {
    return [];
  }
}

/** The owner, or the member themselves (leave): removes a member. Resolves to the updated members. */
export async function removeProjectMember(projectId, email) {
  return membersOf(await sharingRequest(projectId, `/members/${encodeURIComponent(email)}`, { method: 'DELETE' }));
}

/**
 * Editors+: a public preview link. Resolves to `{ id, token, url, expiresAt }` — the token (inside
 * `url`) is returned only here, never again. `expiresInHours` defaults server-side to 168, max 720.
 */
export function createPreviewLink(projectId, { service, expiresInHours, password, allowStart } = {}) {
  const body = {};
  // Whether whoever opens the link may start all the project's services when they are down (the stopped page's Start).
  if (typeof allowStart === 'boolean') { body.allowStart = allowStart; }
  if (service) { body.service = service; }
  if (expiresInHours) { body.expiresInHours = expiresInHours; }
  // Optional: visitors type it on Flow's password page first; the server keeps only a hash (answers `protected`).
  if (password) { body.password = password; }
  return sharingRequest(projectId, '/preview-links', { method: 'POST', body });
}

/**
 * Editors+: sets or changes (a string) or removes (null) a link's password. A new password signs out everyone who had
 * opened the link. Resolves to the link as the access answer lists it (`protected`, never the password).
 */
export async function setPreviewLinkPassword(projectId, linkId, password) {
  const body = await sharingRequest(projectId, `/preview-links/${encodeURIComponent(linkId)}`, { method: 'PATCH', body: { password } });
  return body?.link || null;
}

/** Editors+: lets (true) or stops (false) visitors of a link starting the project's services. Resolves to the link. */
export async function setPreviewLinkAllowStart(projectId, linkId, allowStart) {
  const body = await sharingRequest(projectId, `/preview-links/${encodeURIComponent(linkId)}`, { method: 'PATCH', body: { allowStart: Boolean(allowStart) } });
  return body?.link || null;
}

/** Editors+: the URL of an existing link again (`{ id, url, expiresAt }`), until it is revoked or expires (410). */
export function getPreviewLinkUrl(projectId, linkId) {
  return sharingRequest(projectId, `/preview-links/${encodeURIComponent(linkId)}/url`, { method: 'GET' });
}

/**
 * Editors+: the Expo Go link of one Expo service (vfs-service src/access/expoGo.js), made or reused: a preview link
 * that opens only that service's Metro, on a host of its own. Resolves to `{ id, service, host, url, expoUrl,
 * expiresAt, reused }`; `expoUrl` (`exps://...`) is what Expo Go opens. 409: this Flow has no domain for phone links;
 * 400: the service is not an Expo app. Revoke it with revokePreviewLink.
 */
export function createExpoGoLink(projectId, service) {
  return sharingRequest(projectId, '/preview-links/expo-go', { method: 'POST', body: service ? { service } : {} });
}

/** Editors+: revokes a public preview link at once. */
export function revokePreviewLink(projectId, linkId) {
  return sharingRequest(projectId, `/preview-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
}

/** A repo-flags or context-repos call vfs-service refused: its words, `status` and `code` (and `connectUrl`). */
export class RepoFlagsError extends Error {
  constructor(message, status, code = null, connectUrl = null) {
    super(message);
    this.name = 'RepoFlagsError';
    this.status = status;
    this.code = code;
    this.connectUrl = connectUrl;
  }
}

async function repoRequest(projectId, path, { method = 'GET', body } = {}) {
  if (!isConfigured()) {
    throw new RepoFlagsError('vfs-service is not configured (vfsServiceUrl is unset)', 0, 'vfs_off');
  }
  let response;
  try {
    response = await getAuthorizedFetch()(projectUrl(projectId, path), {
      method,
      headers: headers(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new RepoFlagsError(`Your sandbox could not be reached (${error?.message || 'network error'}).`, 0, 'unreachable');
  }
  if (response.status === 204) { return null; }
  let answer = null;
  try { answer = await response.json(); } catch { answer = null; }
  if (!response.ok) {
    throw new RepoFlagsError(answer?.error || answer?.message || `vfs-service ${method} ${path} failed (${response.status})`, response.status, answer?.code || null, answer?.connectUrl || null);
  }
  return answer;
}

/**
 * The project's repo flags (a repo is `services/<name>` or `context/<name>`): `{ repos: { [repo]: { readOnly, noBuild,
 * context, setBy, setAt } }, canChange, terminal }`, only flagged repos listed; `canChange` is true for the project's
 * owner only. Resolves to null when this vfs-service has no flags (404, or vfs-service not configured): nothing is
 * flagged then.
 */
export async function getRepoFlags(projectId) {
  if (!isConfigured()) { return null; }
  try {
    return await repoRequest(projectId, '/repo-flags');
  } catch (error) {
    if (error?.status === 404) { return null; }
    throw error;
  }
}

/**
 * The owner: turns a repo's `readOnly` and/or `noBuild` on or off. Resolves to `{ repo, flags, repos }`; 403
 * `owner_only`, 404 `unknown_repo`, 400 `context_fixed` (a context repo stays read-only and unbuilt).
 */
export function setRepoFlags(projectId, repo, { readOnly, noBuild } = {}) {
  const body = { repo };
  if (typeof readOnly === 'boolean') { body.readOnly = readOnly; }
  if (typeof noBuild === 'boolean') { body.noBuild = noBuild; }
  return repoRequest(projectId, '/repo-flags', { method: 'PUT', body });
}

/**
 * Runs a command in a service's preview (vfs-service `POST /run-in-service`, vfs_run_in_service over HTTP; editors):
 * Preview › Try it of a command-line tool sends its typed line (`"miactl --help"`). Resolves to `{ success, command,
 * ran?, exit_code, timed_out, duration_ms, stdout, stderr }` (also when the program exits non-zero); rejects with the
 * error's `status` and `code` (400 bad_request, 409 not_ready: not built since the sandbox woke, 429 busy).
 */
export function runInService(projectId, { service, command, timeoutSecs = undefined }) {
  return repoRequest(projectId, '/run-in-service', { method: 'POST', body: { service, command, ...(timeoutSecs ? { timeoutSecs } : {}) } });
}

/** The project's context repos (`context/<name>`): `[{ name, path, url, branch, sha, flags }]`; none on a 404. */
export async function listContextRepos(projectId) {
  try {
    const answer = await repoRequest(projectId, '/context-repos');
    return Array.isArray(answer?.repos) ? answer.repos : [];
  } catch (error) {
    if (error?.status === 404) { return []; }
    throw error;
  }
}

/**
 * Clones a git repository into `context/<name>` as context only (read-only, never built), with the person's account
 * for its host when it is private. Resolves to `{ name, path, sha, repoUrl, usedAccount, flags }`; errors as the
 * clone's (409 exists, 400 bad URL, `no_account` / `account_refused` with `connectUrl`).
 */
export function addContextRepo(projectId, { repoUrl, name, account } = {}) {
  return repoRequest(projectId, '/context-repos', {
    method: 'POST',
    body: { repoUrl, ...(name ? { name } : {}), ...(account ? { account } : {}) },
  });
}

/** Fetches a context repo and fast-forwards it: `{ name, before, sha, updated, summary }` (409 when it cannot). */
export function refreshContextRepo(projectId, name) {
  return repoRequest(projectId, `/context-repos/${encodeURIComponent(name)}/refresh`, { method: 'POST', body: {} });
}

/** The owner: removes a context repo (its folder and its flags). */
export function removeContextRepo(projectId, name) {
  return repoRequest(projectId, `/context-repos/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/**
 * Which of `paths` (project-relative) exist: one POST /stat-many per 500 paths. Returns a Set of the existing ones.
 * @param {string} projectId
 * @param {string[]} paths
 * @returns {Promise<Set<string>>}
 */
export async function existingPaths(projectId, paths) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const found = new Set();
  for (let offset = 0; offset < paths.length; offset += 500) {
    // eslint-disable-next-line no-await-in-loop
    const response = await getAuthorizedFetch()(projectUrl(projectId, '/stat-many'), {
      method: 'POST',
      headers: headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ paths: paths.slice(offset, offset + 500) }),
    });
    if (!response.ok) {
      throw new Error(`vfs-service stat-many failed (${response.status})`);
    }
    // eslint-disable-next-line no-await-in-loop
    for (const entry of await response.json()) {
      if (entry && entry.stat && !entry.error) { found.add(entry.path); }
    }
  }
  return found;
}

/** An upload vfs-service refused or could not take: `status` (413: over a limit), `code` when it named one. */
export class UploadError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Sends a zip to vfs-service's POST /upload, extracted into `targetPath` (project-relative), with a conflict policy
 * for files that exist: overwrite | skip | rename. One commit in the target's history. Resolves to the server's
 * answer: `{ fileCount, created, overwritten, renamed, skipped, failed, files: [{ path, status, requested? }] }`.
 */
export async function uploadArchive(projectId, { targetPath, archive, conflict = 'overwrite', signal } = {}) {
  if (!isConfigured()) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const form = new FormData();
  form.append('path', targetPath || '.');
  form.append('extract', 'true');
  form.append('archiveType', 'zip');
  form.append('conflict', conflict);
  form.append('file', archive, 'upload.zip');
  const response = await getAuthorizedFetch()(projectUrl(projectId, '/upload'), {
    method: 'POST',
    headers: headers(),
    body: form,
    signal,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const fallback = response.status === 403 ? 'You can only view this project.' : response.status === 413 ? 'The upload is larger than the server takes.' : `Upload failed (${response.status}).`;
    throw new UploadError(body.error || body.message || fallback, response.status, body.code);
  }
  return body;
}

export default {
  markProjectVfsBacked,
  isVfsBackedProject,
  registerVfsService,
  getVfsServiceNames,
  createProject,
  writeFile,
  patchFile,
  readFile,
  readProjectMetadata,
  findProjectByConversationId,
  writeDefaultProjectMetadata,
  updateProjectMetadata,
  fetchServiceFiles,
  listFilePaths,
  listFolder,
  fetchAllFiles,
  getHistory,
  getProvenance,
  getProvenanceSummary,
  recordProvenanceEvent,
  grepProject,
  getCommitDiff,
  rollback,
  rollbackAll,
  listVersions,
  saveVersion,
  restoreVersion,
  deleteVersion,
  getWorkingDiff,
  commitCheckpoint,
  getProjectAccess,
  addProjectMember,
  suggestPeople,
  removeProjectMember,
  createPreviewLink,
  setPreviewLinkAllowStart,
  getPreviewLinkUrl,
  setPreviewLinkPassword,
  revokePreviewLink,
  getRepoFlags,
  setRepoFlags,
  listContextRepos,
  addContextRepo,
  refreshContextRepo,
  removeContextRepo,
};
